import { forwardRef, type ButtonHTMLAttributes } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg" | "icon";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-zinc-900 text-white shadow-raise hover:bg-zinc-800 active:bg-zinc-900 disabled:bg-zinc-400 disabled:shadow-none",
  secondary:
    "glass text-zinc-800 hover:bg-white/80 disabled:text-zinc-400 disabled:hover:bg-white/60",
  ghost: "text-zinc-600 hover:bg-white/70 hover:text-zinc-900 disabled:text-zinc-400 disabled:hover:bg-transparent",
  danger: "bg-red-600 text-white shadow-raise hover:bg-red-700 disabled:bg-red-300 disabled:shadow-none",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 px-3 text-[13px] rounded-lg",
  md: "h-10 px-4 text-sm rounded-lg",
  lg: "h-11 px-5 text-sm rounded-xl",
  // Square, no horizontal padding. A `px-0` passed through className could not
  // win against the size padding above, since both are single-class selectors.
  icon: "h-9 w-9 text-[13px] rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "secondary", size = "md", loading, className = "", children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex select-none items-center justify-center gap-1.5 font-medium leading-none transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/25 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      {loading && (
        <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
        </svg>
      )}
      {children}
    </button>
  );
});
